import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { AxiosError } from 'axios';
import { useState } from 'react';

const verifyOnboarding = async ({ payload, token }) => {
  const response = await apiClient.post(
    '/emaratax/verify',
    {
      tin: payload.tin,
      email: payload.email,
      mobile: payload.mobile || null,
    },
    {
      headers: {
        'x-marmin-token': token,
      },
    },
  );
  return response.data;
};

export const useVerifyOnboardingHandler = ({ onSuccess, onError, token }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const mutation = useMutation({
    mutationFn: (payload) => verifyOnboarding({ payload, token }),
    onSuccess: (data) => {
      const isVerified = `${data?.msg_code ?? ''}` === '200';
      const message = data?.msg_desc || '';

      if (!isVerified) {
        onError && onError();
        setErrorMessage(message);
        dispatch(showSnackbar({ message: message, severity: 'error' }));
        return;
      }

      dispatch(showSnackbar({ message: message, severity: 'success' }));
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      onError && onError();
      if (error instanceof AxiosError && error.response) {
        const { status, data } = error.response;
        if (status >= 400 && status < 500) {
          if (status === 400) {
            setError(data.errors);
            return;
          }
          const message =
            data?.errors?.message ||
            data?.message ||
            'The applicant details entered do not match the details on EmaraTax. Please enter the correct details and re-verify.';
          setErrorMessage(message);
          dispatch(showSnackbar({ message, severity: 'error' }));
        } else {
          const message =
            data?.errors?.message || data?.message || error?.message || 'An error occurred during verification';
          setErrorMessage(message);
          dispatch(
            showSnackbar({
              message: message,
              severity: 'error',
            }),
          );
        }
      } else {
        const message = error?.message || 'An error occurred during verification';
        setErrorMessage(message);
        dispatch(
          showSnackbar({
            message: message,
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (payload) => {
    mutation.mutate(payload);
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    errorMessage: errorMessage,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
